import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ArrowRight, ArrowLeft, X, SkipForward } from 'lucide-react';
import { theme } from '../styles/theme';
import { useTour, TourStep } from '../context/TourContext';

const TooltipContainer = styled.div<{ 
  top: number; 
  left: number; 
  placement: string;
}>`
  position: absolute;
  top: ${({ top }) => top}px;
  left: ${({ left }) => left}px;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.xl};
  max-width: 320px;
  min-width: 280px;
  width: fit-content;
  z-index: ${theme.zIndex.modal + 2};
  opacity: 0;
  animation: fadeInScale 0.3s ease-out forwards;
  
  @keyframes fadeInScale {
    from {
      opacity: 0;
      transform: scale(0.95) ${({ placement }) => {
        switch (placement) {
          case 'top': return 'translateY(10px)';
          case 'bottom': return 'translateY(-10px)';
          case 'left': return 'translateX(10px)';
          case 'right': return 'translateX(-10px)';
          default: return 'translateY(10px)';
        }
      }};
    }
    to {
      opacity: 1;
      transform: scale(1) translate(0);
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border-style: solid;
    
    ${({ placement }) => {
      switch (placement) {
        case 'top':
          return `
            top: 100%;
            left: 50%;
            margin-left: -8px;
            border-width: 8px 8px 0 8px;
            border-color: ${theme.colors.surface} transparent transparent transparent;
          `;
        case 'bottom':
          return `
            bottom: 100%;
            left: 50%;
            margin-left: -8px;
            border-width: 0 8px 8px 8px;
            border-color: transparent transparent ${theme.colors.surface} transparent;
          `;
        case 'left':
          return `
            left: 100%;
            top: 50%;
            margin-top: -8px;
            border-width: 8px 0 8px 8px;
            border-color: transparent transparent transparent ${theme.colors.surface};
          `;
        case 'right':
          return `
            right: 100%;
            top: 50%;
            margin-top: -8px;
            border-width: 8px 8px 8px 0;
            border-color: transparent ${theme.colors.surface} transparent transparent;
          `;
        default:
          return `
            top: 100%;
            left: 50%;
            margin-left: -8px;
            border-width: 8px 8px 0 8px;
            border-color: ${theme.colors.surface} transparent transparent transparent;
          `;
      }
    }}
  }
`;

const TooltipHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[5]} ${theme.spacing[5]} 0;
`;

const StepCounter = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
  background: ${theme.colors.primary[100]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: ${theme.colors.textMuted};
  cursor: pointer;
  border-radius: ${theme.borderRadius.base};
  transition: all ${theme.transitions.fast};
  
  &:hover {
    background: ${theme.colors.border};
    color: ${theme.colors.textPrimary};
  }
`;

const TooltipContent = styled.div`
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
`;

const TooltipTitle = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin-bottom: ${theme.spacing[3]};
  line-height: ${theme.typography.lineHeight.tight};
`;

const TooltipDescription = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  line-height: ${theme.typography.lineHeight.normal};
  margin: 0;
`;

const TooltipFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${theme.spacing[5]} ${theme.spacing[5]};
  border-top: 1px solid ${theme.colors.border};
  margin-top: ${theme.spacing[4]};
  padding-top: ${theme.spacing[4]};
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;
  
  @media (max-width: 400px) {
    flex-direction: column;
    align-items: stretch;
    gap: ${theme.spacing[3]};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const TourButton = styled.button<{ variant?: 'primary' | 'secondary' | 'ghost' }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  border: 1px solid;
  white-space: nowrap;
  flex-shrink: 0;
  
  ${({ variant = 'secondary' }) => {
    switch (variant) {
      case 'primary':
        return `
          background: ${theme.colors.primary[500]};
          color: white;
          border-color: ${theme.colors.primary[500]};
          
          &:hover {
            background: ${theme.colors.primary[600]};
            border-color: ${theme.colors.primary[600]};
            transform: translateY(-1px);
          }
          
          &:disabled {
            background: ${theme.colors.border};
            border-color: ${theme.colors.border};
            color: ${theme.colors.textMuted};
            cursor: not-allowed;
            transform: none;
          }
        `;
      case 'ghost':
        return `
          background: transparent;
          color: ${theme.colors.textMuted};
          border-color: transparent;
          
          &:hover {
            background: ${theme.colors.border};
            color: ${theme.colors.textPrimary};
          }
        `;
      default:
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.textSecondary};
          border-color: ${theme.colors.border};
          
          &:hover {
            background: ${theme.colors.primary[50]};
            color: ${theme.colors.primary[600]};
            border-color: ${theme.colors.primary[300]};
          }
          
          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        `;
    }
  }}
`;

const ProgressDots = styled.div`
  display: flex;
  gap: ${theme.spacing[1]};
`;

const ProgressDot = styled.div<{ isActive: boolean; isCompleted: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: ${theme.borderRadius.full};
  background: ${({ isActive, isCompleted }) => 
    isCompleted ? theme.colors.success[500] :
    isActive ? theme.colors.primary[500] : 
    theme.colors.border
  };
  transition: all ${theme.transitions.fast};
`;

interface TourTooltipProps {
  step: TourStep;
  targetElement: HTMLElement | null;
  elementBounds: { top: number; left: number; width: number; height: number } | null;
}

const TourTooltip: React.FC<TourTooltipProps> = ({ step, targetElement, elementBounds }) => {
  const { tourState, nextStep, prevStep, skipTour, isFirstStep, isLastStep } = useTour();
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = React.useRef<HTMLDivElement>(null);

  const calculatePosition = () => {
    if (!elementBounds) {
      // Center the tooltip if no target element
      setPosition({
        top: window.innerHeight / 2 - 100,
        left: window.innerWidth / 2 - 150,
      });
      return;
    }

    // Get actual tooltip dimensions, fallback to estimated values
    const tooltipWidth = tooltipRef.current?.offsetWidth || 320;
    const tooltipHeight = tooltipRef.current?.offsetHeight || 240;
    const spacing = 16;

    let top = 0;
    let left = 0;

    switch (step.placement) {
      case 'top':
        top = elementBounds.top - tooltipHeight - spacing;
        left = elementBounds.left + (elementBounds.width / 2) - (tooltipWidth / 2);
        break;
      case 'bottom':
        top = elementBounds.top + elementBounds.height + spacing;
        left = elementBounds.left + (elementBounds.width / 2) - (tooltipWidth / 2);
        break;
      case 'left':
        top = elementBounds.top + (elementBounds.height / 2) - (tooltipHeight / 2);
        left = elementBounds.left - tooltipWidth - spacing;
        break;
      case 'right':
        top = elementBounds.top + (elementBounds.height / 2) - (tooltipHeight / 2);
        left = elementBounds.left + elementBounds.width + spacing;
        break;
      case 'center':
        top = window.innerHeight / 2 - 100;
        left = window.innerWidth / 2 - 150;
        break;
      default:
        top = elementBounds.top - tooltipHeight - spacing;
        left = elementBounds.left + (elementBounds.width / 2) - (tooltipWidth / 2);
    }

    // Ensure tooltip stays within viewport
    const padding = 16;
    top = Math.max(padding, Math.min(top, window.innerHeight - tooltipHeight - padding));
    left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding));

    setPosition({ top, left });
  };

  useEffect(() => {
    // Initial calculation
    calculatePosition();
  }, [elementBounds, step.placement]);

  useEffect(() => {
    // Recalculate after component renders with actual dimensions
    const timer = setTimeout(() => {
      calculatePosition();
    }, 10);
    return () => clearTimeout(timer);
  }, [tourState.currentStep]);

  const handleNext = () => {
    if (step.beforeNext && !step.beforeNext()) {
      return;
    }
    nextStep();
  };

  return (
    <TooltipContainer
      ref={tooltipRef}
      top={position.top}
      left={position.left}
      placement={step.placement}
    >
      <TooltipHeader>
        <StepCounter>
          Step {tourState.currentStep + 1} of {tourState.steps.length}
        </StepCounter>
        <CloseButton onClick={skipTour}>
          <X size={16} />
        </CloseButton>
      </TooltipHeader>

      <TooltipContent>
        <TooltipTitle>{step.title}</TooltipTitle>
        <TooltipDescription>{step.content}</TooltipDescription>
      </TooltipContent>

      <TooltipFooter>
        <ProgressDots>
          {tourState.steps.map((_, index) => (
            <ProgressDot
              key={index}
              isActive={index === tourState.currentStep}
              isCompleted={index < tourState.currentStep}
            />
          ))}
        </ProgressDots>

        <ButtonGroup>
          {(step.showSkip !== false) && !isLastStep() && (
            <TourButton variant="ghost" onClick={skipTour}>
              <SkipForward size={14} />
              Skip Tour
            </TourButton>
          )}
          
          {(step.showPrev !== false) && !isFirstStep() && (
            <TourButton onClick={prevStep}>
              <ArrowLeft size={14} />
              Previous
            </TourButton>
          )}
          
          {(step.showNext !== false) && (
            <TourButton variant="primary" onClick={handleNext}>
              {isLastStep() ? 'Finish' : 'Next'}
              {!isLastStep() && <ArrowRight size={14} />}
            </TourButton>
          )}
        </ButtonGroup>
      </TooltipFooter>
    </TooltipContainer>
  );
};

export default TourTooltip;